import type { Dict } from '../../i18n';

const dict: Dict = {
  en: {
    // Topbar
    heading: 'Clients',
    subtitleAll: 'Global client directory',
    subtitleFiltered: "Clients owned by this agent",
    subtitleCount_one: '{{count}} client',
    subtitleCount_other: '{{count}} clients',
    searchPlaceholder: 'Search anything: name, CIF, email, phone, city…',
    newClientBtn: '+ New client',
    reassignBtn: 'Reassign clients',
    clearFilterBtn: 'Clear agent filter',
    filterAgentLabel: 'Filter by agent',
    filterAllAgents: 'All agents',
    filterUnassigned: 'Unassigned',
    filterCorrection: 'Correction list',
    colStatus: 'Status',
    statusBlocked: 'blocked',
    statusCorrection: 'correct',
    statusOk: 'ok',

    // Table headers
    colClient: 'Client',
    colCifVat: 'ID / NIF',
    colVat: 'VAT',
    colCity: 'City',
    colCountry: 'Country',
    colEmail: 'Email',
    colOwnerAgent: 'Owner agent',
    colOrders: 'Orders',
    colLastOrder: 'Last order',
    colDeliveries: 'Delivery addresses',

    // VAT validity (dot tooltip)
    vatValid: 'VAT valid',
    vatInvalid: 'VAT invalid',
    vatUnavailable: 'VIES unavailable',
    vatUnverified: 'Not validated yet',
    vatNotApplicable: 'Non-EU — not checked',
    vatCheckedOn: 'checked {{date}}',

    // Cell fallbacks
    unassigned: 'Unassigned',

    // Row actions
    viewEditBtn: 'View / Edit',
    deleteBtn: 'Delete',
    deleteConfirm: 'Delete client "{{name}}"? This cannot be undone.',
    deleteArchivedNotice:
      '"{{name}}" has orders, so it was archived instead of deleted.',

    // Empty / loading / error states
    loadingClients: 'Loading clients…',
    emptyTitle: 'No clients yet',
    emptyHint: 'Create the first client with "+ New client".',
    noMatchTitle: 'No matching clients',
    noMatchHint: 'Try a different name, CIF or email, or clear the agent filter.',
  },
  es: {
    // Topbar
    heading: 'Clientes',
    subtitleAll: 'Directorio global de clientes',
    subtitleFiltered: 'Clientes de este agente',
    subtitleCount_one: '{{count}} cliente',
    subtitleCount_other: '{{count}} clientes',
    searchPlaceholder: 'Busca cualquier dato: nombre, CIF, correo, teléfono, ciudad…',
    newClientBtn: '+ Nuevo cliente',
    reassignBtn: 'Reasignar clientes',
    clearFilterBtn: 'Quitar filtro de agente',
    filterAgentLabel: 'Filtrar por agente',
    filterAllAgents: 'Todos los agentes',
    filterUnassigned: 'Sin asignar',
    filterCorrection: 'Lista de corrección',
    colStatus: 'Estado',
    statusBlocked: 'bloqueado',
    statusCorrection: 'corregir',
    statusOk: 'ok',

    // Table headers
    colClient: 'Cliente',
    colCifVat: 'ID / NIF',
    colVat: 'IVA',
    colCity: 'Ciudad',
    colCountry: 'País',
    colEmail: 'Correo electrónico',
    colOwnerAgent: 'Agente propietario',
    colOrders: 'Pedidos',
    colLastOrder: 'Último pedido',
    colDeliveries: 'Direcciones de entrega',

    // Validez del IVA (tooltip del punto)
    vatValid: 'IVA válido',
    vatInvalid: 'IVA no válido',
    vatUnavailable: 'VIES no disponible',
    vatUnverified: 'Aún sin validar',
    vatNotApplicable: 'No UE — sin comprobar',
    vatCheckedOn: 'comprobado {{date}}',

    // Cell fallbacks
    unassigned: 'Sin asignar',

    // Row actions
    viewEditBtn: 'Ver / Editar',
    deleteBtn: 'Eliminar',
    deleteConfirm: '¿Eliminar el cliente "{{name}}"? Esta acción no se puede deshacer.',
    deleteArchivedNotice:
      '"{{name}}" tiene pedidos, por lo que se archivó en lugar de eliminarse.',

    // Empty / loading / error states
    loadingClients: 'Cargando clientes…',
    emptyTitle: 'Sin clientes aún',
    emptyHint: 'Crea el primer cliente con "+ Nuevo cliente".',
    noMatchTitle: 'No se encontró nada',
    noMatchHint: 'Prueba con otro nombre, CIF o correo, o quita el filtro de agente.',
  },
  de: {
    // Topbar
    heading: 'Kunden',
    subtitleAll: 'Globales Kundenverzeichnis',
    subtitleFiltered: 'Kunden dieses Agenten',
    subtitleCount_one: '{{count}} Kunde',
    subtitleCount_other: '{{count}} Kunden',
    searchPlaceholder: 'Beliebig suchen: Name, CIF, E-Mail, Telefon, Stadt…',
    newClientBtn: '+ Neuer Kunde',
    reassignBtn: 'Kunden neu zuweisen',
    clearFilterBtn: 'Agentenfilter zurücksetzen',
    filterAgentLabel: 'Nach Agent filtern',
    filterAllAgents: 'Alle Agenten',
    filterUnassigned: 'Nicht zugewiesen',
    filterCorrection: 'Korrekturliste',
    colStatus: 'Status',
    statusBlocked: 'gesperrt',
    statusCorrection: 'korrigieren',
    statusOk: 'ok',

    // Table headers
    colClient: 'Kunde',
    colCifVat: 'NIF/ID',
    colVat: 'USt-IdNr.',
    colCity: 'Stadt',
    colCountry: 'Land',
    colEmail: 'E-Mail',
    colOwnerAgent: 'Zuständiger Agent',
    colOrders: 'Bestellungen',
    colLastOrder: 'Letzte Bestellung',
    colDeliveries: 'Lieferadressen',

    // VAT validity (dot tooltip)
    vatValid: 'USt-IdNr. gültig',
    vatInvalid: 'USt-IdNr. ungültig',
    vatUnavailable: 'VIES nicht verfügbar',
    vatUnverified: 'Noch nicht geprüft',
    vatNotApplicable: 'Nicht-EU — nicht geprüft',
    vatCheckedOn: 'geprüft {{date}}',

    // Cell fallbacks
    unassigned: 'Nicht zugewiesen',

    // Row actions
    viewEditBtn: 'Ansehen / Bearbeiten',
    deleteBtn: 'Löschen',
    deleteConfirm: 'Kunde "{{name}}" löschen? Dies kann nicht rückgängig gemacht werden.',
    deleteArchivedNotice:
      '"{{name}}" hat Bestellungen und wurde daher archiviert statt gelöscht.',

    // Empty / loading / error states
    loadingClients: 'Kunden werden geladen…',
    emptyTitle: 'Noch keine Kunden',
    emptyHint: 'Erstellen Sie den ersten Kunden mit "+ Neuer Kunde".',
    noMatchTitle: 'Keine passenden Kunden',
    noMatchHint: 'Versuchen Sie einen anderen Namen, CIF oder eine andere E-Mail, oder setzen Sie den Agentenfilter zurück.',
  },
};

export default dict;
